import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Divider, Grid, Paper, TextField, Typography } from '@mui/material';
import { apiService } from '@/services/api';
import { useMessage } from '@/contexts/MessageContext';
import { logger } from '@/utils/logger';
import { shouldSkipInterSandboxWebhookRequests } from '@/utils/interSandboxSchedule';

type TipoWebhook = 'pix-pagamento' | 'boleto-pagamento';

type WebhookConfig = {
  webhookUrl?: string;
  criacao?: string;
};

function stripWebhookToken(url: string): string {
  const u = String(url || '').trim();
  if (!u) return '';
  try {
    const parsed = new URL(u);
    if (parsed.searchParams.has('token')) {
      parsed.searchParams.delete('token');
    }
    // remove trailing ? if empty
    const out = parsed.toString();
    return out.replace(/\?$/, '');
  } catch {
    // fallback simples: remove token=... da query
    return u
      .replace(/([?&])token=[^&]+(&)?/i, (_m, sep, amp) => (sep === '?' && amp ? '?' : sep))
      .replace(/[?&]$/, '');
  }
}

function isoNowMinusHours(h: number): string {
  return new Date(Date.now() - h * 3600_000).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

type InterWebhooksConfigPageProps = {
  /** Quando true, omite o título principal (uso dentro de Integração bancária). */
  embedded?: boolean;
};

function useInterSandboxWebhookSkip(): boolean {
  const [skip, setSkip] = useState(() => shouldSkipInterSandboxWebhookRequests());
  useEffect(() => {
    const tick = () => setSkip(shouldSkipInterSandboxWebhookRequests());
    tick();
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, []);
  return skip;
}

const InterWebhooksConfigPage: React.FC<InterWebhooksConfigPageProps> = ({ embedded }) => {
  const { showSuccess, showError } = useMessage();
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
  }, [showError, showSuccess]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [globalUrl, setGlobalUrl] = useState('');
  const [cobranca, setCobranca] = useState<WebhookConfig | null>(null);
  const [pix, setPix] = useState<WebhookConfig | null>(null);

  const [cbTipo, setCbTipo] = useState<TipoWebhook>('boleto-pagamento');
  const [cbInicio, setCbInicio] = useState<string>(() => isoNowMinusHours(24));
  const [cbFim, setCbFim] = useState<string>(() =>
    new Date().toISOString().replace(/\.\d{3}Z$/, 'Z')
  );
  const [cbCodigoSolicitacao, setCbCodigoSolicitacao] = useState('');
  const [cbCodigoTransacao, setCbCodigoTransacao] = useState('');
  const [callbacksResult, setCallbacksResult] = useState<any>(null);

  const sandboxWebhookSkip = useInterSandboxWebhookSkip();

  const reload = useCallback(async (): Promise<void> => {
    if (shouldSkipInterSandboxWebhookRequests()) {
      return;
    }
    setLoading(true);
    try {
      const [boletoRes, pixRes] = await Promise.all([
        apiService.getInterWebhook('boleto-pagamento'),
        apiService.getInterWebhook('pix-pagamento')
      ]);
      setCobranca((boletoRes.data || null) as WebhookConfig | null);
      setPix((pixRes.data || null) as WebhookConfig | null);
      const anyUrl =
        String((boletoRes.data as any)?.webhookUrl || '').trim() ||
        String((pixRes.data as any)?.webhookUrl || '').trim();
      if (anyUrl) setGlobalUrl(stripWebhookToken(anyUrl));
    } catch (e) {
      logger.error('Error loading Inter webhooks:', e);
      showErrorRef.current('Falha ao carregar webhooks do Inter.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (cancelled || sandboxWebhookSkip) return;
      await reload();
    })();
    return () => {
      cancelled = true;
    };
  }, [sandboxWebhookSkip, reload]);

  const saveBoth = async () => {
    if (shouldSkipInterSandboxWebhookRequests()) return;
    const url = globalUrl.trim();
    if (!url) {
      showError('Informe a URL do webhook.');
      return;
    }
    setSaving(true);
    try {
      const [boletoRes, pixRes] = await Promise.all([
        apiService.putInterWebhook('boleto-pagamento', { webhookUrl: url }),
        apiService.putInterWebhook('pix-pagamento', { webhookUrl: url })
      ]);
      setCobranca((boletoRes.data || null) as WebhookConfig | null);
      setPix((pixRes.data || null) as WebhookConfig | null);
      showSuccessRef.current('Webhook do Inter salvo (boleto + pix).');
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Falha ao salvar webhook';
      logger.error('Error saving Inter webhooks:', e);
      showErrorRef.current(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const deleteOne = async (tipo: TipoWebhook) => {
    if (shouldSkipInterSandboxWebhookRequests()) return;
    setSaving(true);
    try {
      await apiService.deleteInterWebhook(tipo);
      if (tipo === 'boleto-pagamento') setCobranca(null);
      if (tipo === 'pix-pagamento') setPix(null);
      showSuccessRef.current(`Webhook removido (${tipo}).`);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Falha ao remover webhook';
      logger.error('Error deleting Inter webhook:', e);
      showErrorRef.current(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const fetchCallbacks = async () => {
    if (shouldSkipInterSandboxWebhookRequests()) return;
    setSaving(true);
    try {
      const res = await apiService.listInterWebhookCallbacks(cbTipo, {
        dataHoraInicio: cbInicio.trim(),
        dataHoraFim: cbFim.trim(),
        ...(cbTipo === 'pix-pagamento'
          ? cbCodigoSolicitacao.trim()
            ? { txid: cbCodigoSolicitacao.trim() }
            : {}
          : cbCodigoSolicitacao.trim()
            ? { codigoSolicitacao: cbCodigoSolicitacao.trim() }
            : {})
      });
      setCallbacksResult(res.data);
      showSuccess('Callbacks carregados.');
    } catch (e: any) {
      const data = e?.response?.data;
      const interDetail =
        data?.interData?.detail ||
        data?.interData?.title ||
        (typeof data === 'string' ? data : undefined);
      const msg =
        interDetail ||
        data?.message ||
        e?.message ||
        'Falha ao consultar callbacks. Tente novamente em instantes.';
      logger.error('Error listing callbacks:', e);
      showError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const retry = async () => {
    if (shouldSkipInterSandboxWebhookRequests()) return;
    setSaving(true);
    try {
      if (cbTipo === 'pix-pagamento') {
        showError(
          'Reenvio de callbacks do Pix (retry) não está disponível nesta tela (depende do schema do Inter).'
        );
        return;
      } else {
        const ids = cbCodigoSolicitacao
          .split(/[\s,]+/)
          .map(s => s.trim())
          .filter(Boolean);
        if (ids.length === 0) {
          showError('Informe pelo menos um codigoSolicitacao (separado por vírgula ou espaço).');
          return;
        }
        await apiService.retryInterWebhookCallbacks(cbTipo, { codigoSolicitacao: ids } as any);
      }
      showSuccess('Reenvio solicitado ao Inter.');
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Falha ao reenviar callbacks';
      logger.error('Error retry callbacks:', e);
      showError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const statusText = useMemo(() => {
    const b = cobranca?.webhookUrl ? 'OK' : 'não cadastrado';
    const p = pix?.webhookUrl ? 'OK' : 'não cadastrado';
    return `Boleto: ${b} | Pix: ${p}`;
  }, [cobranca, pix]);

  const formDisabled = sandboxWebhookSkip || loading || saving;

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        {!embedded && <Typography variant="h6">Webhooks Banco Inter</Typography>}
        <Typography variant="body2" color="text.secondary">
          Configuração centralizada (URL única) para callbacks do Inter.
        </Typography>
      </Box>

      {sandboxWebhookSkip && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Os dados de webhooks não foram carregados: em ambiente local ou de staging as chamadas
          usam o sandbox do Banco Inter, que só aceita requisições de segunda a sexta-feira, das
          08:00 às 19:59 (horário de Brasília). Fora desse período, nenhuma operação desta tela é
          enviada ao sandbox.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Status: {statusText}
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="URL do webhook (global)"
            value={globalUrl}
            onChange={e => setGlobalUrl(e.target.value)}
            disabled={formDisabled}
            placeholder="https://billing.seu-dominio.com/webhooks/inter/cobranca"
          />
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => void reload()} disabled={formDisabled}>
            Recarregar
          </Button>
          <Button variant="contained" onClick={() => void saveBoth()} disabled={formDisabled}>
            {saving ? 'Salvando...' : 'Salvar (boleto + pix)'}
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Button
          variant="outlined"
          color="error"
          onClick={() => void deleteOne('boleto-pagamento')}
          disabled={formDisabled}
        >
          Excluir webhook BOLETO
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => void deleteOne('pix-pagamento')}
          disabled={formDisabled}
        >
          Excluir webhook PIX
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Consultar callbacks enviados / Reenviar
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Tipo"
              value={cbTipo}
              onChange={e => setCbTipo(e.target.value as TipoWebhook)}
              select
              SelectProps={{ native: true }}
              disabled={formDisabled}
            >
              <option value="boleto-pagamento">boleto-pagamento</option>
              <option value="pix-pagamento">pix-pagamento</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="dataHoraInicio (RFC3339)"
              value={cbInicio}
              onChange={e => setCbInicio(e.target.value)}
              disabled={formDisabled}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="dataHoraFim (RFC3339)"
              value={cbFim}
              onChange={e => setCbFim(e.target.value)}
              disabled={formDisabled}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={
                cbTipo === 'pix-pagamento'
                  ? 'txid (PIX) — filtro'
                  : 'codigoSolicitacao (BOLETO) — filtro/reenviar'
              }
              value={cbCodigoSolicitacao}
              onChange={e => setCbCodigoSolicitacao(e.target.value)}
              disabled={formDisabled}
              helperText="Para reenviar, pode informar múltiplos separados por vírgula ou espaço."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="(não usado)"
              value={cbCodigoTransacao}
              onChange={e => setCbCodigoTransacao(e.target.value)}
              disabled
              helperText="A documentação oficial para cobrança de boleto usa codigoSolicitacao no retry."
            />
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => void fetchCallbacks()}
              disabled={formDisabled}
            >
              Consultar callbacks
            </Button>
            <Button variant="contained" onClick={() => void retry()} disabled={formDisabled}>
              Reenviar callbacks
            </Button>
          </Grid>
        </Grid>
      </Box>

      {callbacksResult && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Resultado (JSON)
          </Typography>
          <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
            {JSON.stringify(callbacksResult, null, 2)}
          </pre>
        </Box>
      )}
    </Paper>
  );
};

export default InterWebhooksConfigPage;
